/**
 * Mergeport Ordering
 * ## Introduction    Mergeport Ordering is a channel manager for online ordering and delivery services. With a single interface, we establish a connection between a network of POS systems and multiple service providers in a standardized format.    ## Access details and developer account   If not already done, **contact us** for a developer account. You will receive a ```providerId``` as identification of your platform as well as a ```restaurantId``` of  demo restaurant. In addition, we will provide you with credentials to log in to our MERGEPORT Controller interface. The ```restaurantId``` changes for each restaurant.     ## Data flow   MERGEPORT provides the possibility to synchronize orders and articles with a POS system. The POS system linked to the specified ```restaurantId``` will be notified in realtime about incoming orders or cancellations and process them correspondingly. MERGEPORT will deliver the orders also if the POS system is not available at the time of creation.    ### Order status  Once the order has been dispatched, the POS can process it. First, the POS will set the status to ```fetchedByPOS``` to mark it as read. After processing, the POS is supposed to update the status to ```acceptedByPOS``` or ```rejectedByPOS```. ```acceptedByPOS``` means, the gastronomy will take care about the delivery. You can check the status by either fetching the order via the HTTP GET endpoint or by setting a web hook for the notification. Please contact the MERGEPORT admins upon provider creation for this feature or use the corresponding endpoint to set the web hook (callbackUrl) via API..     ### Cancellation   Should the order be cancelled before the POS processes the order, the order will be terminated and not forwarded to the POS anymore. If the POS has already started the processing, it will be assigned with a request to cancel the order.    ### Payment   What is specified in the ```payment``` object of the payment defines how the POS handles the order. The parameter ```payOnDelivery``` defines whether the order is already paid (```payOnDelivery=false```) or still needs to be paid (```payOnDelivery=true```). The ```brand```attribute states where the payment came from, e.g. paypal and the ```type``` specifies if it is an online (```CARD```) or offline (```CASH```) payment.    ### Prices and currencies   All prices are expressed as a structured object, with the currency code, e.g. \"EUR\" and the value in the atomic part of that currency, typically \"cent\". 3� will be represented as 300.    ### Menu, categories and items   Depending on the implementation of the POS, MERGEPORT stores a shadow copy of the products (internally also called ```items```), menu categories of the POS. The POS is supposed to update the products at MERGEPORT upon every change. Within the MERGEPORT controller, the gastronomer is able to enrich the products, menu and categories with meta information such as localized names and descriptions as well as images. By requesting the menu of the restaurant, the provider can import the (enriched) products. Almost any kind of variation of products can be represented by this structure. The ```type``` of a product distinguishes products and toppings. Both of which can be associated to menus and categories.  With the parameter ```parentIds```, products/toppings can be linked to one or multiple items in a hierarchical order.  To post an order, the provider needs to set the item ID of the product as the ```remoteCode``` of the product (see POST Order). The products of an order are included in the ```products``` object in a hierarchical order. For example, toppings can be included as a child of the parent product.    ### Matching of the products   When an order is placed, the POS will try to match the contained products with the articles stored in its database. If a ```remoteCode``` is set by the platform, the POS will match it against its interal product ids. If this process fails, it will try to match a product by name or create a dummy fallback product. Additionally, if the implementation allows it, the gastronomer is able to map the products of the POS with those of the platform in the MERGEPORT Controller.  ## Responses   Typically, our responses are being returned with the **HTTP status codes** ```200``` **OK**, ```400``` **Bad Request**, ```401``` **Unauthorized**, ```403``` **Forbidden**. We have three response types, you can easily check the response for the following fields&#58;   - ```[data]``` - for read operations. The response contains an object or array of the requested data   - ```success``` - for generic operations. The success property contains a message describing the operation result.   - ```error``` - for minor errors. The error property contains a code and a message  ## Request validation   All requests will be validated. The following cases will lead to a validation error:   - Missing required properties   - Unknown properties in the request body   - Invalid data format.    Important&#58; date-time fields and parameters must be formatted as UTC ISO 8601 strings, e.g. '2000-01-01T10:10:10.123Z'. 
 *
 * OpenAPI spec version: 4.0.8
 * Contact: hello@mergeport.com
 *
 * NOTE: This class is auto generated by the swagger code generator program.
 * https://github.com/swagger-api/swagger-codegen.git
 * Do not edit the class manually.
 *//* tslint:disable:no-unused-variable member-ordering */

import { Inject, Injectable, Optional }                      from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams,
         HttpResponse, HttpEvent }                           from '@angular/common/http';
import { CustomHttpUrlEncodingCodec }                        from '../encoder';

import { Observable }                                        from 'rxjs';

import { InlineResponse200 } from '../model/inlineResponse200';
import { InlineResponse2001 } from '../model/inlineResponse2001';
import { InlineResponse2002 } from '../model/inlineResponse2002';
import { InlineResponse2003 } from '../model/inlineResponse2003';
import { InlineResponse202 } from '../model/inlineResponse202';
import { InlineResponse400 } from '../model/inlineResponse400';
import { MenuItems } from '../model/menuItems';
import { Order } from '../model/order';
import { OrdersSubscribeBody } from '../model/ordersSubscribeBody';

import { BASE_PATH, COLLECTION_FORMATS }                     from '../variables';
import { Configuration }                                     from '../configuration';


@Injectable()
export class ServiceProviderAPIService {

    protected basePath = 'https://ordering.mergeport.com/v4';
    public defaultHeaders = new HttpHeaders();
    public configuration = new Configuration();

    constructor(protected httpClient: HttpClient, @Optional()@Inject(BASE_PATH) basePath: string, @Optional() configuration: Configuration) {
        if (basePath) {
            this.basePath = basePath;
        }
        if (configuration) {
            this.configuration = configuration;
            this.basePath = basePath || configuration.basePath || this.basePath;
        }
    }

    /**
     * @param consumes string[] mime-types
     * @return true: consumes contains 'multipart/form-data', false: otherwise
     */
    private canConsumeForm(consumes: string[]): boolean {
        const form = 'multipart/form-data';
        for (const consume of consumes) {
            if (form === consume) {
                return true;
            }
        }
        return false;
    }


    /**
     * Endpoint to notify a POS about status updates for an already dispatched
     * Status change of an already dispatched order. Will be forwarded to the POS and processed correspondingly.
     * @param providerId provider ID from mergeport
     * @param restaurantId remote Restaurant ID
     * @param orderId remote Order ID
     * @param body default POS Plugin API - Update Order Status
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public changeOrderStatus(providerId: string, restaurantId: string, orderId: string, body?: any, observe?: 'body', reportProgress?: boolean): Observable<any>;
    public changeOrderStatus(providerId: string, restaurantId: string, orderId: string, body?: any, observe?: 'response', reportProgress?: boolean): Observable<HttpResponse<any>>;
    public changeOrderStatus(providerId: string, restaurantId: string, orderId: string, body?: any, observe?: 'events', reportProgress?: boolean): Observable<HttpEvent<any>>;
    public changeOrderStatus(providerId: string, restaurantId: string, orderId: string, body?: any, observe: any = 'body', reportProgress: boolean = false ): Observable<any> {

        if (providerId === null || providerId === undefined) {
            throw new Error('Required parameter providerId was null or undefined when calling changeOrderStatus.');
        }

        if (restaurantId === null || restaurantId === undefined) {
            throw new Error('Required parameter restaurantId was null or undefined when calling changeOrderStatus.');
        }

        if (orderId === null || orderId === undefined) {
            throw new Error('Required parameter orderId was null or undefined when calling changeOrderStatus.');
        }


        let headers = this.defaultHeaders;

        // to determine the Accept header
        let httpHeaderAccepts: string[] = [
        ];
        const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        if (httpHeaderAcceptSelected != undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }

        // to determine the Content-Type header
        const consumes: string[] = [
            'application/json'
        ];
        const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
        if (httpContentTypeSelected != undefined) {
            headers = headers.set('Content-Type', httpContentTypeSelected);
        }

        return this.httpClient.request<any>('put',`${this.basePath}/hooks/main/${encodeURIComponent(String(providerId))}/orderStatus/${encodeURIComponent(String(restaurantId))}/${encodeURIComponent(String(orderId))}`,
            {
                body: body,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Endpoint to fetch the categories of the specified restaurant
     * Returns a plain list of all categories ( warm dishes, drinks, ... ) of the specified restaurant. The categories are synchronized with the POS and can be enriched in the MERGEPORT controller interface. Hierarchical on top of a product by categoryIds.
     * @param providerId provider ID from mergeport
     * @param restaurantId remote restaurantId provided by Mergeport
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public getMainCategories(providerId: string, restaurantId: string, observe?: 'body', reportProgress?: boolean): Observable<Array<InlineResponse2003>>;
    public getMainCategories(providerId: string, restaurantId: string, observe?: 'response', reportProgress?: boolean): Observable<HttpResponse<Array<InlineResponse2003>>>;
    public getMainCategories(providerId: string, restaurantId: string, observe?: 'events', reportProgress?: boolean): Observable<HttpEvent<Array<InlineResponse2003>>>;
    public getMainCategories(providerId: string, restaurantId: string, observe: any = 'body', reportProgress: boolean = false ): Observable<any> {

        if (providerId === null || providerId === undefined) {
            throw new Error('Required parameter providerId was null or undefined when calling getMainCategories.');
        }

        if (restaurantId === null || restaurantId === undefined) {
            throw new Error('Required parameter restaurantId was null or undefined when calling getMainCategories.');
        }

        let headers = this.defaultHeaders;

        // to determine the Accept header
        let httpHeaderAccepts: string[] = [
            'application/json'
        ];
        const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        if (httpHeaderAcceptSelected != undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }

        // to determine the Content-Type header
        const consumes: string[] = [
        ];

        return this.httpClient.request<Array<InlineResponse2003>>('get',`${this.basePath}/hooks/main/${encodeURIComponent(String(providerId))}/${encodeURIComponent(String(restaurantId))}/categories`,
            {
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Endpoint to fetch the menus of the specified restaurant
     * Returns a plain list of all menus ( daily, wine, ... ) of the specified restaurant. The menus are synchronized with the POS and can be enriched in the MERGEPORT controller interface. Hierarchical on top of a product by menuIds.
     * @param providerId provider ID from mergeport
     * @param restaurantId remote restaurantId provided by Mergeport
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public getMainMenus(providerId: string, restaurantId: string, observe?: 'body', reportProgress?: boolean): Observable<Array<InlineResponse2002>>;
    public getMainMenus(providerId: string, restaurantId: string, observe?: 'response', reportProgress?: boolean): Observable<HttpResponse<Array<InlineResponse2002>>>;
    public getMainMenus(providerId: string, restaurantId: string, observe?: 'events', reportProgress?: boolean): Observable<HttpEvent<Array<InlineResponse2002>>>;
    public getMainMenus(providerId: string, restaurantId: string, observe: any = 'body', reportProgress: boolean = false ): Observable<any> {

        if (providerId === null || providerId === undefined) {
            throw new Error('Required parameter providerId was null or undefined when calling getMainMenus.');
        }

        if (restaurantId === null || restaurantId === undefined) {
            throw new Error('Required parameter restaurantId was null or undefined when calling getMainMenus.');
        }

        let headers = this.defaultHeaders;

        // to determine the Accept header
        let httpHeaderAccepts: string[] = [
            'application/json'
        ];
        const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        if (httpHeaderAcceptSelected != undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }

        // to determine the Content-Type header
        const consumes: string[] = [
        ];

        return this.httpClient.request<Array<InlineResponse2002>>('get',`${this.basePath}/hooks/main/${encodeURIComponent(String(providerId))}/${encodeURIComponent(String(restaurantId))}/menus`,
            {
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Endpoint to fetch an order of the specified restaurant
     * Returns an order in the Pos system which shows status or payment related info.
     * @param providerId provider ID from mergeport
     * @param restaurantId remote restaurantId provided by Mergeport
     * @param orderId remote Order ID
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public getMainOrder(providerId: string, restaurantId: string, orderId: string, observe?: 'body', reportProgress?: boolean): Observable<InlineResponse200>;
    public getMainOrder(providerId: string, restaurantId: string, orderId: string, observe?: 'response', reportProgress?: boolean): Observable<HttpResponse<InlineResponse200>>;
    public getMainOrder(providerId: string, restaurantId: string, orderId: string, observe?: 'events', reportProgress?: boolean): Observable<HttpEvent<InlineResponse200>>;
    public getMainOrder(providerId: string, restaurantId: string, orderId: string, observe: any = 'body', reportProgress: boolean = false ): Observable<any> {

        if (providerId === null || providerId === undefined) {
            throw new Error('Required parameter providerId was null or undefined when calling getMainOrder.');
        }

        if (restaurantId === null || restaurantId === undefined) {
            throw new Error('Required parameter restaurantId was null or undefined when calling getMainOrder.');
        }

        if (orderId === null || orderId === undefined) {
            throw new Error('Required parameter orderId was null or undefined when calling getMainOrder.');
        }

        let headers = this.defaultHeaders;

        // to determine the Accept header
        let httpHeaderAccepts: string[] = [
            'application/json'
        ];
        const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        if (httpHeaderAcceptSelected != undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }

        // to determine the Content-Type header
        const consumes: string[] = [
        ];

        return this.httpClient.request<InlineResponse200>('get',`${this.basePath}/hooks/main/${encodeURIComponent(String(providerId))}/${encodeURIComponent(String(restaurantId))}/orders/${encodeURIComponent(String(orderId))}`,
            {
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Endpoint to fetch the menu items of the specified restaurant
     * Returns a plain list of all products ( items ) of the specified restaurant. The articles are synchronized with the POS and can be enriched in the MERGEPORT controller interface. Hierarchical order by parentId.
     * @param providerId provider ID from mergeport
     * @param restaurantId remote restaurantId provided by Mergeport
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public getMenuItems(providerId: string, restaurantId: string, observe?: 'body', reportProgress?: boolean): Observable<Array<InlineResponse2001>>;
    public getMenuItems(providerId: string, restaurantId: string, observe?: 'response', reportProgress?: boolean): Observable<HttpResponse<Array<InlineResponse2001>>>;
    public getMenuItems(providerId: string, restaurantId: string, observe?: 'events', reportProgress?: boolean): Observable<HttpEvent<Array<InlineResponse2001>>>;
    public getMenuItems(providerId: string, restaurantId: string, observe: any = 'body', reportProgress: boolean = false ): Observable<any> {

        if (providerId === null || providerId === undefined) {
            throw new Error('Required parameter providerId was null or undefined when calling getMenuItems.');
        }

        if (restaurantId === null || restaurantId === undefined) {
            throw new Error('Required parameter restaurantId was null or undefined when calling getMenuItems.');
        }

        let headers = this.defaultHeaders;

        // to determine the Accept header
        let httpHeaderAccepts: string[] = [
            'application/json'
        ];
        const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        if (httpHeaderAcceptSelected != undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }

        // to determine the Content-Type header
        const consumes: string[] = [
        ];

        return this.httpClient.request<Array<InlineResponse2001>>('get',`${this.basePath}/hooks/main/${encodeURIComponent(String(providerId))}/${encodeURIComponent(String(restaurantId))}/items`,
            {
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Endpoint to fetch the menu of the specified restaurant
     * Returns a plain list of all products ( items ) of the specified restaurant. The articles are synchronized with the POS and can be enriched in the MERGEPORT controller interface. Hierarchical order by parentId.
     * @param providerId provider ID from mergeport
     * @param restaurantId remote restaurantId provided by Mergeport
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public getMenuItemsOld(providerId: string, restaurantId: string, observe?: 'body', reportProgress?: boolean): Observable<MenuItems>;
    public getMenuItemsOld(providerId: string, restaurantId: string, observe?: 'response', reportProgress?: boolean): Observable<HttpResponse<MenuItems>>;
    public getMenuItemsOld(providerId: string, restaurantId: string, observe?: 'events', reportProgress?: boolean): Observable<HttpEvent<MenuItems>>;
    public getMenuItemsOld(providerId: string, restaurantId: string, observe: any = 'body', reportProgress: boolean = false ): Observable<any> {

        if (providerId === null || providerId === undefined) {
            throw new Error('Required parameter providerId was null or undefined when calling getMenuItemsOld.');
        }

        if (restaurantId === null || restaurantId === undefined) {
            throw new Error('Required parameter restaurantId was null or undefined when calling getMenuItemsOld.');
        }

        let headers = this.defaultHeaders;

        // to determine the Accept header
        let httpHeaderAccepts: string[] = [
            'application/json'
        ];
        const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        if (httpHeaderAcceptSelected != undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }

        // to determine the Content-Type header
        const consumes: string[] = [
        ];

        return this.httpClient.request<MenuItems>('get',`${this.basePath}/hooks/main/${encodeURIComponent(String(providerId))}/menu/${encodeURIComponent(String(restaurantId))}`,
            {
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Endpoint to notify a POS about status updates for an already dispatched order
     * Status change of an already dispatched order. Will be forwarded to the POS and processed correspondingly.
     * @param providerId provider ID from mergeport
     * @param restaurantId remote Restaurant ID
     * @param orderId remote Order ID
     * @param body default POS Plugin API - Update Order Status
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public mainChangeOrderStatus(providerId: string, restaurantId: string, orderId: string, body?: any, observe?: 'body', reportProgress?: boolean): Observable<any>;
    public mainChangeOrderStatus(providerId: string, restaurantId: string, orderId: string, body?: any, observe?: 'response', reportProgress?: boolean): Observable<HttpResponse<any>>;
    public mainChangeOrderStatus(providerId: string, restaurantId: string, orderId: string, body?: any, observe?: 'events', reportProgress?: boolean): Observable<HttpEvent<any>>;
    public mainChangeOrderStatus(providerId: string, restaurantId: string, orderId: string, body?: any, observe: any = 'body', reportProgress: boolean = false ): Observable<any> {

        if (providerId === null || providerId === undefined) {
            throw new Error('Required parameter providerId was null or undefined when calling mainChangeOrderStatus.');
        }

        if (restaurantId === null || restaurantId === undefined) {
            throw new Error('Required parameter restaurantId was null or undefined when calling mainChangeOrderStatus.');
        }

        if (orderId === null || orderId === undefined) {
            throw new Error('Required parameter orderId was null or undefined when calling mainChangeOrderStatus.');
        }


        let headers = this.defaultHeaders;

        // to determine the Accept header
        let httpHeaderAccepts: string[] = [
        ];
        const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        if (httpHeaderAcceptSelected != undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }

        // to determine the Content-Type header
        const consumes: string[] = [
            'application/json'
        ];
        const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
        if (httpContentTypeSelected != undefined) {
            headers = headers.set('Content-Type', httpContentTypeSelected);
        }

        return this.httpClient.request<any>('put',`${this.basePath}/hooks/main/${encodeURIComponent(String(providerId))}/${encodeURIComponent(String(restaurantId))}/orders/${encodeURIComponent(String(orderId))}`,
            {
                body: body,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Endpoint to dispatch a new order to the POS
     * POST Request to dispatch a new order to the POS linked to the restaurant
     * @param providerId provider ID from mergeport
     * @param restaurantId online Restaurant ID
     * @param body default POS Plugin API - OrderDispatch
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public mainPlaceOrder(providerId: string, restaurantId: string, body?: Order, observe?: 'body', reportProgress?: boolean): Observable<InlineResponse202>;
    public mainPlaceOrder(providerId: string, restaurantId: string, body?: Order, observe?: 'response', reportProgress?: boolean): Observable<HttpResponse<InlineResponse202>>;
    public mainPlaceOrder(providerId: string, restaurantId: string, body?: Order, observe?: 'events', reportProgress?: boolean): Observable<HttpEvent<InlineResponse202>>;
    public mainPlaceOrder(providerId: string, restaurantId: string, body?: Order, observe: any = 'body', reportProgress: boolean = false ): Observable<any> {

        if (providerId === null || providerId === undefined) {
            throw new Error('Required parameter providerId was null or undefined when calling mainPlaceOrder.');
        }

        if (restaurantId === null || restaurantId === undefined) {
            throw new Error('Required parameter restaurantId was null or undefined when calling mainPlaceOrder.');
        }


        let headers = this.defaultHeaders;

        // to determine the Accept header
        let httpHeaderAccepts: string[] = [
            'application/json'
        ];
        const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        if (httpHeaderAcceptSelected != undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }

        // to determine the Content-Type header
        const consumes: string[] = [
            'application/json'
        ];
        const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
        if (httpContentTypeSelected != undefined) {
            headers = headers.set('Content-Type', httpContentTypeSelected);
        }

        return this.httpClient.request<InlineResponse202>('post',`${this.basePath}/hooks/main/${encodeURIComponent(String(providerId))}/${encodeURIComponent(String(restaurantId))}/orders`,
            {
                body: body,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Endpoint to set an URL to get order status updates from the POS
     * POST an URL to get status updates for orders from the POS linked to the restaurant   ### Mergeport Controller   This could also be **configured** in the **provider settings** in the mergeport controller
     * @param providerId provider ID from mergeport
     * @param restaurantId online Restaurant ID
     * @param body 
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public mainSetOrderStatusURL(providerId: string, restaurantId: string, body?: OrdersSubscribeBody, observe?: 'body', reportProgress?: boolean): Observable<any>;
    public mainSetOrderStatusURL(providerId: string, restaurantId: string, body?: OrdersSubscribeBody, observe?: 'response', reportProgress?: boolean): Observable<HttpResponse<any>>;
    public mainSetOrderStatusURL(providerId: string, restaurantId: string, body?: OrdersSubscribeBody, observe?: 'events', reportProgress?: boolean): Observable<HttpEvent<any>>;
    public mainSetOrderStatusURL(providerId: string, restaurantId: string, body?: OrdersSubscribeBody, observe: any = 'body', reportProgress: boolean = false ): Observable<any> {

        if (providerId === null || providerId === undefined) {
            throw new Error('Required parameter providerId was null or undefined when calling mainSetOrderStatusURL.');
        }

        if (restaurantId === null || restaurantId === undefined) {
            throw new Error('Required parameter restaurantId was null or undefined when calling mainSetOrderStatusURL.');
        }


        let headers = this.defaultHeaders;

        // to determine the Accept header
        let httpHeaderAccepts: string[] = [
        ];
        const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        if (httpHeaderAcceptSelected != undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }

        // to determine the Content-Type header
        const consumes: string[] = [
            'application/json'
        ];
        const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
        if (httpContentTypeSelected != undefined) {
            headers = headers.set('Content-Type', httpContentTypeSelected);
        }

        return this.httpClient.request<any>('post',`${this.basePath}/hooks/main/${encodeURIComponent(String(providerId))}/${encodeURIComponent(String(restaurantId))}/orders/subscribe`,
            {
                body: body,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Endpoint to dispatch a new order to the POS
     * POST Request to dispatch a new order to the POS linked to the restaurant
     * @param providerId provider ID from mergeport
     * @param restaurantId online Restaurant ID
     * @param body default POS Plugin API - OrderDispatch
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public placeOrder(providerId: string, restaurantId: string, body?: Order, observe?: 'body', reportProgress?: boolean): Observable<InlineResponse202>;
    public placeOrder(providerId: string, restaurantId: string, body?: Order, observe?: 'response', reportProgress?: boolean): Observable<HttpResponse<InlineResponse202>>;
    public placeOrder(providerId: string, restaurantId: string, body?: Order, observe?: 'events', reportProgress?: boolean): Observable<HttpEvent<InlineResponse202>>;
    public placeOrder(providerId: string, restaurantId: string, body?: Order, observe: any = 'body', reportProgress: boolean = false ): Observable<any> {

        if (providerId === null || providerId === undefined) {
            throw new Error('Required parameter providerId was null or undefined when calling placeOrder.');
        }

        if (restaurantId === null || restaurantId === undefined) {
            throw new Error('Required parameter restaurantId was null or undefined when calling placeOrder.');
        }


        let headers = this.defaultHeaders;

        // to determine the Accept header
        let httpHeaderAccepts: string[] = [
            'application/json'
        ];
        const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        if (httpHeaderAcceptSelected != undefined) {
            headers = headers.set('Accept', httpHeaderAcceptSelected);
        }

        // to determine the Content-Type header
        const consumes: string[] = [
            'application/json'
        ];
        const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
        if (httpContentTypeSelected != undefined) {
            headers = headers.set('Content-Type', httpContentTypeSelected);
        }

        return this.httpClient.request<InlineResponse202>('post',`${this.basePath}/hooks/main/${encodeURIComponent(String(providerId))}/order/${encodeURIComponent(String(restaurantId))}`,
            {
                body: body,
                withCredentials: this.configuration.withCredentials,
                headers: headers,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

}
